import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon, MoreIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { Dispatch, FC, Fragment, SetStateAction, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { WorkflowBlock } from 'dashboard/models/workflow-block';
import { WorkflowModule, WorkflowModuleStatus } from 'dashboard/models/workflow-module';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Sticker, { LabelBackgrounds } from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

const StyledCard = styled(Card)`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: ${Spacing[5]} ${Spacing[6]};
`;

const TitleContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const RightContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  white-space: nowrap;
  margin-left: ${Spacing[3]};
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
`;

const ModuleTitle = styled(Link)`
  font-weight: 500;
  font-size: 20px;
  line-height: 150%;
  display: flex;
  align-items: center;
  letter-spacing: 0.15px;
  color: ${({ theme }) => theme.color.text.primary};
  overflow: hidden;

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const TimestampText = styled(Paragraph)`
  font-weight: 400;
  font-size: 12px;
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const StyledTooltip = styled(Tooltip).attrs(() => ({ placement: 'left', trigger: 'hover' }))`
  margin-top: ${Spacing[1]};
  margin-left: ${Spacing[2]};

  & > div {
    max-width: 360px;
  }
`;

const CardBody = styled('div')`
  grid-template-columns: 1fr 200px;
  display: grid;
  margin-top: ${Spacing[2]};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  grid-auto-flow: row;
  > * {
    font-size: 14px;
  }
`;

const LabelText = styled(Paragraph)`
  font-style: normal;
  font-weight: 400;
  color: ${({ theme }) => theme.color.text.secondary};
`;

const ButtonsContainer = styled.div`
  margin-top: ${Spacing[4]};
  display: flex;
  justify-content: flex-end;

  & > button:first-child {
    width: 72px;
  }
  & > button:nth-child(2) {
    width: 32px;
    margin-left: ${Spacing[2]};
  }
`;

const ModuleCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  & > div + div {
    margin-top: ${Spacing[4]};
  }
`;

interface ModuleCardsProps {
  setModuleToActivate: Dispatch<SetStateAction<WorkflowModule | null>>;
  setModuleToArchive: Dispatch<SetStateAction<WorkflowModule | null>>;
  setModuleToDeactivate: Dispatch<SetStateAction<WorkflowModule | null>>;
  workflowModulesWithRelationships: {
    workflowModule: WorkflowModule;
    workflowBlock: WorkflowBlock;
  }[];
}

const ModuleCards: FC<ModuleCardsProps> = ({
  setModuleToActivate,
  setModuleToArchive,
  setModuleToDeactivate,
  workflowModulesWithRelationships,
}: ModuleCardsProps) => {
  return (
    <ModuleCardsContainer>
      {workflowModulesWithRelationships.map(({ workflowModule, workflowBlock }) => (
        <ModuleCard
          key={workflowModule.id}
          onArchiveModule={setModuleToArchive}
          onActivateModule={setModuleToActivate}
          onDeactivateModule={setModuleToDeactivate}
          workflowModule={workflowModule}
          workflowBlock={workflowBlock}
        />
      ))}
    </ModuleCardsContainer>
  );
};

interface ModuleCardProps {
  onArchiveModule: (workflowModule: WorkflowModule) => void;
  onDeactivateModule: (workflowModule: WorkflowModule) => void;
  onActivateModule: (workflowModule: WorkflowModule) => void;
  workflowModule: WorkflowModule;
  workflowBlock: WorkflowBlock;
}

const ModuleCard: FC<ModuleCardProps> = ({
  onArchiveModule,
  onDeactivateModule,
  onActivateModule,
  workflowModule,
  workflowBlock,
}) => {
  const {
    id: moduleId,
    attributes: { status: moduleStatus, name: moduleName },
  } = workflowModule;

  const navigate = useNavigate();

  const handleClickEdit = () => {
    navigate(`/dashboard/workflows/modules/${moduleId}?version=${workflowBlock.id}`);
  };

  const canWrite = usePermission(Permission.WorkflowWrite);

  const moreMenuItems = [
    canWrite && moduleStatus !== WorkflowModuleStatus.Inactive && (
      <DropdownMenu.Item
        id='module-card__dropdown-deactivate'
        key='deactivate'
        onClick={() => onDeactivateModule(workflowModule)}
      >
        Deactivate
      </DropdownMenu.Item>
    ),
    canWrite && moduleStatus !== WorkflowModuleStatus.Active && (
      <DropdownMenu.Item
        id='module-card__dropdown-activate'
        key='activate'
        onClick={() => onActivateModule(workflowModule)}
      >
        Activate
      </DropdownMenu.Item>
    ),
    canWrite && (
      <DropdownMenu.Item
        id='workflow-card__dropdown-archive'
        key='archive'
        variant='danger'
        onClick={() => onArchiveModule(workflowModule)}
      >
        Archive
      </DropdownMenu.Item>
    ),
  ].filter(Boolean);

  const statusDescriptor = useMemo(() => {
    return `Last edited at ${DateTime.fromISO(workflowModule.attributes['updated-at']).toFormat(
      DATE_AND_TIME_TZ_FORMAT,
    )}`;
  }, [workflowModule.attributes]);

  return (
    <StyledCard>
      <TitleContainer>
        <LeftContainer>
          <ModuleTitle to={`/dashboard/workflows/modules/${moduleId}/${workflowBlock.id}`}>
            <span data-test={`workflow-module-card__name__${moduleId}`}>{moduleName}</span>
          </ModuleTitle>
          {workflowModule.attributes.description && (
            <StyledTooltip iconTrigger={<CircleInfoIcon />}>
              <div>{workflowModule.attributes.description}</div>
            </StyledTooltip>
          )}
        </LeftContainer>
        <RightContainer>
          <TimestampText>{statusDescriptor}</TimestampText>
        </RightContainer>
      </TitleContainer>
      <CardBody>
        <InfoGrid>
          <LabelText>Status</LabelText>
          <LabelText>Appears in Workflows</LabelText>
          <div>
            <StatusSticker
              status={workflowModule.attributes.status === WorkflowModuleStatus.Active}
            />
          </div>
          <Paragraph color='primary'>
            {workflowModule.attributes.callers?.length
              ? workflowModule.attributes.callers.map(
                  ({ 'source-name': name, 'source-id': workflowToken }, idx) => (
                    <Fragment key={workflowToken}>
                      {idx > 0 && ', '}
                      <Link key={idx} to={`/dashboard/workflows/${workflowToken}`}>
                        {name || workflowToken}
                      </Link>
                    </Fragment>
                  ),
                )
              : 'None'}
          </Paragraph>
        </InfoGrid>
        <ButtonsContainer>
          <Button
            variant='basic'
            width='auto'
            onClick={handleClickEdit}
            id='workflow-module-card__edit_button'
          >
            {canWrite ? 'Edit' : 'View'}
          </Button>
          {moreMenuItems.length > 0 && (
            <DropdownMenu
              trigger={
                <Button
                  aria-label={`More actions for ${moduleName}`}
                  id={`workflow-module_card__more-actions-${moduleId}`}
                  variant='basic'
                  width='icon'
                >
                  <MoreIcon />
                </Button>
              }
            >
              {moreMenuItems}
            </DropdownMenu>
          )}
        </ButtonsContainer>
      </CardBody>
    </StyledCard>
  );
};

interface StatusStickerProps {
  status: boolean;
}

const StatusSticker: FC<StatusStickerProps> = ({ status }: StatusStickerProps) => {
  const getStickerStatus = (): [Partial<LabelBackgrounds>, string] => {
    switch (status) {
      case true: {
        return ['green', 'Active'];
      }
      case false: {
        return ['grey', 'Inactive'];
      }
      default: {
        return ['darkGrey', 'n/a'];
      }
    }
  };

  const [variantType, statusMessage] = getStickerStatus();

  return (
    <Sticker variant={variantType} data-test='workflow-module-status-sticker__sticker'>
      {statusMessage}
    </Sticker>
  );
};

export default ModuleCards;
